import { z } from 'zod';
import { calendarDateSchema } from '@/features/listing-filters/calendarDate';

export const INTENT_STATUSES = [
  'Pending',
  'Queued',
  'Sent',
  'Suppressed',
  'Errored',
  'Deferred',
  'Expired',
  'FailedToCheck',
] as const;

/**
 * `channel` is a free VarChar on the intent too, so these are the values the
 * engine writes today. An unlisted value still shows in the table; it just is
 * not offered as a filter option.
 */
export const CHANNELS = ['email', 'sms', 'rcs'] as const;

/**
 * `recipientType` is a free VarChar on the intent rather than a database enum,
 * so this list is maintained by hand. An unlisted value still shows in the
 * table; it just is not offered as a filter option, which is how `InformUser`
 * went missing here while 12 intents in dev already used it.
 *
 * ⚠️ The canonical list is `WorkflowRecipientType` in
 * `packages/workflow/src/constants.ts`. It cannot be imported: `@astro/workflow`
 * is server-only and is not a dependency of `apps/admin`. Check it against this
 * when adding a recipient type.
 */
export const RECIPIENT_TYPES = ['ClinicPetParent', 'ClinicUser', 'InformUser'] as const;

/**
 * Filters only. Pagination is deliberately excluded: it lives in the URL beside
 * these but must not be saved, since returning to page 7 of a list you last saw
 * days ago is disorienting.
 */
export const mailroomFiltersSchema = z.object({
  status: z.array(z.enum(INTENT_STATUSES)).optional().catch(undefined),
  channel: z.array(z.string().min(1)).optional().catch(undefined),
  recipientType: z.array(z.string().min(1)).optional().catch(undefined),
  // Matches a recipient id or a workflow name. Capped at 255 to match the api;
  // a workflow name does not fit in the old recipient-only 30.
  search: z.string().min(1).max(255).optional().catch(undefined),
  scheduledFrom: calendarDateSchema.optional().catch(undefined),
  scheduledTo: calendarDateSchema.optional().catch(undefined),
  hasError: z.boolean().optional().catch(undefined),
  hasSuppression: z.boolean().optional().catch(undefined),
});

export type MailroomFilterValues = z.infer<typeof mailroomFiltersSchema>;

export const mailroomSearchSchema = mailroomFiltersSchema.extend({
  page: z.number().int().min(1).optional().catch(undefined),
});
